import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Store, MapPin, Phone, Clock, Mail, Globe, Save, CheckCircle2, Loader2, CreditCard, Wifi } from "lucide-react";
import CGVSettings from "../components/CGVSettings";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopSettingsPage() {
  const [settings, setSettings] = useState({
    shop_name: "",
    shop_address: "",
    shop_phone: "",
    shop_hours: "",
    shop_email: "",
    shop_website: "",
    tpe_ip: "",
    tpe_port: "8888",
    opening_hours: {},
    slot_minutes: 60,
    blocked_slots: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/admin/shop-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        shop_name:    res.data.shop_name    || "",
        shop_address: res.data.shop_address || "",
        shop_phone:   res.data.shop_phone   || "",
        shop_hours:   res.data.shop_hours   || "",
        shop_email:   res.data.shop_email   || "",
        shop_website: res.data.shop_website || "",
        tpe_ip:       res.data.tpe_ip       || "",
        tpe_port:     res.data.tpe_port     || "8888",
        opening_hours: res.data.opening_hours || {},
        slot_minutes:  res.data.slot_minutes  || 60,
        blocked_slots: res.data.blocked_slots || [],
      });
    } catch (err) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!settings.shop_address) {
      toast.error("L'adresse du magasin est obligatoire");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/api/admin/shop-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      toast.success("Paramètres enregistrés");
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    {
      key: "shop_name",
      label: "Nom du magasin",
      placeholder: "AMCPhone Martinique",
      icon: Store,
      required: false,
    },
    {
      key: "shop_address",
      label: "Adresse complète *",
      placeholder: "Centre Commercial la Galleria, 97232 Le Lamentin, Martinique",
      icon: MapPin,
      required: true,
      hint: "Affiché dans les emails clients et sur le formulaire de prise en charge",
    },
    {
      key: "shop_phone",
      label: "Téléphone",
      placeholder: "0596 00 00 00",
      icon: Phone,
      type: "tel",
    },
    {
      key: "shop_hours",
      label: "Horaires d'ouverture",
      placeholder: "Lun-Sam 9h-18h",
      icon: Clock,
    },
    {
      key: "shop_email",
      label: "Email de contact",
      placeholder: "contact@amcphone-martinique.fr",
      icon: Mail,
      type: "email",
    },
    {
      key: "shop_website",
      label: "Site web",
      placeholder: "https://amcphone-martinique.fr",
      icon: Globe,
      type: "url",
    },
    {
      key: "tpe_ip",
      label: "IP du TPE (PAX A920)",
      placeholder: "192.168.1.40",
      icon: CreditCard,
      hint: "Adresse IP locale de votre terminal de paiement",
    },
    {
      key: "tpe_port",
      label: "Port du TPE",
      placeholder: "8888",
      icon: CreditCard,
      type: "number",
      hint: "Port de communication (défaut : 8888)",
    },
  ];

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-chivo text-2xl font-bold text-foreground flex items-center gap-2">
            <Store className="w-6 h-6 text-primary" />
            Paramètres du magasin
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Ces informations apparaissent dans les emails envoyés aux clients et sur la page de prise en charge.
          </p>
        </div>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Informations du magasin</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-5">
                {fields.map(({ key, label, placeholder, icon: Icon, required, hint, type }) => (
                  <div key={key} className="space-y-1.5">
                    <Label htmlFor={key} className="flex items-center gap-1.5 text-sm font-medium">
                      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                      {label}
                    </Label>
                    <Input
                      id={key}
                      type={type || "text"}
                      value={settings[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={placeholder}
                      className={`bg-secondary/50 border-border focus:border-primary ${
                        required && !settings[key] ? "border-red-500/50" : ""
                      }`}
                    />
                    {hint && (
                      <p className="text-xs text-muted-foreground">{hint}</p>
                    )}
                  </div>
                ))}

                {/* Aperçu adresse */}
                {settings.shop_address && (
                  <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                    <p className="text-xs font-medium text-primary mb-2">
                      Aperçu — affiché dans les emails et la prise en charge :
                    </p>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="text-sm text-foreground">
                        <p className="font-medium">{settings.shop_name || "Votre magasin"}</p>
                        <p className="text-muted-foreground">{settings.shop_address}</p>
                        {settings.shop_phone && <p className="text-muted-foreground">{settings.shop_phone}</p>}
                        {settings.shop_hours && <p className="text-muted-foreground">{settings.shop_hours}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* CGV */}
                <div className="p-4 rounded-xl bg-secondary/30 border border-border">
                  <CGVSettings />
                </div>

                {/* Test TPE */}
                {settings.tpe_ip && (
                  <div className="p-4 rounded-xl bg-secondary/30 border border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">Terminal PAX A920</span>
                        <span className="text-xs text-muted-foreground">{settings.tpe_ip}:{settings.tpe_port}</span>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem("token");
                            const res = await axios.get(`${API_URL}/api/tpe/status`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.data.online) {
                              toast.success("✅ TPE connecté et joignable !");
                            } else {
                              toast.error("❌ TPE hors ligne");
                            }
                          } catch {
                            toast.error("Erreur de connexion au TPE");
                          }
                        }}
                        className="text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 font-medium transition-colors"
                      >
                        <Wifi className="w-3.5 h-3.5 inline mr-1" />
                        Tester la connexion
                      </button>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className={`w-full ${saved ? "bg-emerald-500 hover:bg-emerald-600" : "bg-primary hover:bg-primary/90"}`}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : saved ? (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {saving ? "Enregistrement..." : saved ? "Enregistré !" : "Enregistrer les modifications"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
